/**
 * Polls unread notifications and alerts when a replay is ready.
 * Ensures the user is notified even if they navigated away from the flight screen.
 */
import { onMounted, onUnmounted } from 'vue';
import { fetchNotifications, fetchRemoteFlight, markNotificationRead } from '@/services/sync';
import { isAuthenticated } from '@/services/api';
import { emitReplayReady } from '@/composables/useReplayReadyEvents';
import { showReplayReadyAlert } from '@/utils/replayReadyAlert';
import { setGenerating } from '@/composables/useReplayGenerating';

const POLL_INTERVAL_MS = 10_000;

export type ReplayNotificationsOptions = {
  /** Called when a replay-ready notification is received (e.g. refresh flight list). */
  onReplayReady?: (payload: { remoteId: string; flightName: string }) => void;
};

export function useReplayNotifications(options: ReplayNotificationsOptions = {}): void {
  const { onReplayReady } = options;
  let intervalId: ReturnType<typeof setInterval> | null = null;

  async function handleReplayReadyNotification(
    remoteId: string,
    flightName: string,
    notificationId: string,
  ): Promise<void> {
    setGenerating(remoteId, false);
    const flightRes = await fetchRemoteFlight(remoteId);
    const replayUrl = flightRes.success ? flightRes.data?.replayUrl : null;
    if (replayUrl) {
      emitReplayReady({ remoteId, replayUrl, flightName });
    }
    await showReplayReadyAlert(flightName);
    await markNotificationRead(notificationId);
    onReplayReady?.({ remoteId, flightName });
  }

  function tick(): void {
    if (!isAuthenticated() || document.visibilityState !== 'visible') return;
    fetchNotifications().then(async (res) => {
      if (!res.success || !res.data?.notifications) return;
      for (const n of res.data.notifications) {
        if (n.type !== 'replay_ready' && n.data?.type !== 'replay_ready') continue;
        const remoteId = n.data?.flight_id;
        if (!remoteId) continue;
        const name = n.data?.flight_name ?? 'Flight';
        await handleReplayReadyNotification(remoteId, name, n.id);
      }
    });
  }

  function start(): void {
    if (intervalId != null) return;
    tick();
    intervalId = setInterval(tick, POLL_INTERVAL_MS);
  }

  function stop(): void {
    if (intervalId != null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }

  onMounted(() => {
    start();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        start();
        tick();
      } else {
        stop();
      }
    });
  });

  onUnmounted(stop);
}
